/**
 * Content for the WG-Vermieter page (`/operators`), ported verbatim from the
 * approved website draft's "3e" screen.
 *
 * Kept as data rather than inlined in JSX because it is commercial copy: the
 * prices, the "270 Stunden" claim and the 460+ / 98,4 % / 70+ figures are public
 * commitments, so they should be reviewable in one place.
 *
 * German is the source language of the draft; the English column is a
 * translation for the site's EN toggle.
 */

export type Bilingual = { de: string; en: string };

export const LANDLORD_STATS: { value: string; label: Bilingual }[] = [
  {
    value: "460+",
    label: {
      de: "Zimmer betreiben wir selbst",
      en: "rooms we operate ourselves",
    },
  },
  {
    value: "98,4 %",
    label: {
      de: "Belegung im Portfolio",
      en: "occupancy across the portfolio",
    },
  },
  {
    value: "70+",
    label: {
      de: "Eigentümer verwalten bereits mit coliviq",
      en: "owners already manage with coliviq",
    },
  },
];

export const OWNER_HOW: { name: Bilingual; body: Bilingual }[] = [
  {
    name: { de: "Kein Leerstandsmonat", en: "No empty month" },
    body: {
      de: "Nachmietersuche startet vor dem Auszug — jedes Zimmer einzeln vermarktet.",
      en: "The search for the next tenant starts before the current one leaves — every room marketed on its own.",
    },
  },
  {
    name: { de: "Kein Verwaltungsaufwand", en: "No admin burden" },
    body: {
      de: "Anzeigen, Bewerber, Verträge, Miete und Anliegen laufen digital.",
      en: "Listings, applicants, contracts, rent and issues all run digitally.",
    },
  },
  {
    name: { de: "Bis aufs Zimmer gedacht", en: "Thought through to the room" },
    body: {
      de: "Eigene Miete, Laufzeit und Abrechnung je Zimmer — die Wohnung bleibt eine Einheit.",
      en: "Its own rent, term and statement per room — while the flat stays one unit.",
    },
  },
];

export const PRODUCT_CARDS: {
  /**
   * App screenshot shown inside the browser frame, one per language: the
   * screen's own copy has to match the copy beside it.
   */
  image: Bilingual;
  /** Alt text / the label a missing image falls back to. */
  shot: Bilingual;
  name: Bilingual;
  body: Bilingual;
  dark?: boolean;
}[] = [
  {
    image: {
      de: "/assets/landlord-shot-property-de.webp",
      en: "/assets/landlord-shot-property-en.webp",
    },
    shot: { de: "Objektansicht mit Zimmern", en: "Property view with rooms" },
    name: { de: "Jedes Zimmer auf einen Blick", en: "Every room at a glance" },
    body: {
      de: "Belegung, Miete und offene Punkte — ohne Excel.",
      en: "Occupancy, rent and open items — without a spreadsheet.",
    },
  },
  {
    image: {
      de: "/assets/landlord-shot-overview-de.webp",
      en: "/assets/landlord-shot-overview-en.webp",
    },
    shot: { de: "Eigentümer-Übersicht", en: "Owner overview" },
    name: {
      de: "Vom Interessenten zum Vertrag",
      en: "From enquiry to contract",
    },
    body: {
      de: "Geprüfte Bewerber, Signatur im selben Ablauf.",
      en: "Vetted applicants, signature in the same flow.",
    },
  },
  {
    image: {
      de: "/assets/landlord-shot-tenant-de.webp",
      en: "/assets/landlord-shot-tenant-en.webp",
    },
    shot: { de: "Mieterbereich", en: "Tenant area" },
    name: { de: "Mieter regeln es selbst", en: "Tenants handle it themselves" },
    body: {
      de: "Anliegen, Dokumente und Zahlungen in der App — Ihr Telefon bleibt still.",
      en: "Issues, documents and payments in the app — your phone stays quiet.",
    },
    dark: true,
  },
];

/* ------------------------------------------------------------------ */
/* Pricing                                                             */
/* ------------------------------------------------------------------ */

export type Plan = {
  name: Bilingual;
  /** € per room per month. */
  monthly: number;
  yearly: number;
  badge?: Bilingual;
  inherit?: Bilingual;
  bullets: Bilingual[];
  foot: Bilingual;
};

export const PLANS: Plan[] = [
  {
    name: { de: "Selbstverwaltung", en: "Self-managed" },
    monthly: 19,
    yearly: 16,
    bullets: [
      {
        de: "Eigene Anzeige für jedes Zimmer mit Startdatum",
        en: "Its own listing for every room, with a start date",
      },
      {
        de: "Alle Bewerbungen in einer Pipeline",
        en: "Every application in one pipeline",
      },
      {
        de: "Verträge digital signiert, Dokumente je Zimmer abgelegt",
        en: "Contracts signed digitally, documents filed per room",
      },
    ],
    foot: {
      de: "2 Jahre Laufzeit",
      en: "2-year term",
    },
  },
  {
    name: { de: "Smartverwaltung", en: "Smart management" },
    monthly: 29,
    yearly: 25,
    inherit: {
      de: "Alles aus der Selbstverwaltung, plus:",
      en: "Everything in Self-managed, plus:",
    },
    bullets: [
      {
        de: "Anzeige auf allen gängigen Portalen — schneller neu vermietet",
        en: "Listed on every major portal — re-let faster",
      },
      {
        de: "Bonitätsprüfung — nur passende Bewerber in Ihrer Auswahl",
        en: "Credit checks — only suitable applicants reach your shortlist",
      },
      {
        de: "SEPA-Einzug und Mahnwesen — kein Nachhaken bei der Miete",
        en: "SEPA direct debit and dunning — no chasing rent",
      },
    ],
    foot: { de: "2 Jahre Laufzeit", en: "2-year term" },
  },
  {
    name: { de: "Sorglos-Verwaltung", en: "Fully managed" },
    monthly: 49,
    yearly: 49,
    badge: {
      de: "98 % wählen dieses Paket",
      en: "98 % choose this package",
    },
    inherit: {
      de: "Alles aus der Smartverwaltung, plus:",
      en: "Everything in Smart management, plus:",
    },
    bullets: [
      { de: "Instandhaltungsmanagement", en: "Maintenance management" },
      {
        de: "Anzeigen erstellen und veröffentlichen wir für Sie",
        en: "We create and publish the listings for you",
      },
      {
        de: "Besichtigungen, Auswahl und Übergabe wickeln wir digital ab",
        en: "We handle viewings, selection and handover digitally",
      },
    ],
    foot: {
      de: "2 Jahre Laufzeit · inklusive Onboarding",
      en: "2-year term · onboarding included",
    },
  },
];

/** Comparison table: [name, note, self, smart, full] — 1 = included. */
export const COMPARISON: {
  group: Bilingual;
  rows: [Bilingual, Bilingual, number, number, number][];
}[] = [
  {
    group: { de: "Plattform & Dokumente", en: "Platform & documents" },
    rows: [
      [
        { de: "Eigentümer-Plattform", en: "Owner platform" },
        {
          de: "Alle Objekte, Zimmer und Verträge an einer Stelle",
          en: "Every property, room and contract in one place",
        },
        1,
        1,
        1,
      ],
      [
        {
          de: "Interaktives Eigentümer-Dashboard",
          en: "Interactive owner dashboard",
        },
        {
          de: "Belegung, Miete und offene Punkte je Zimmer",
          en: "Occupancy, rent and open items per room",
        },
        1,
        1,
        1,
      ],
      [
        { de: "Mieter-Dashboard", en: "Tenant dashboard" },
        {
          de: "Mieter sehen Vertrag, Zahlungen und Anliegen selbst",
          en: "Tenants see their contract, payments and issues themselves",
        },
        1,
        1,
        1,
      ],
      [
        { de: "Dokumentenablage", en: "Document storage" },
        {
          de: "Verträge, Protokolle und Nachweise je Zimmer",
          en: "Contracts, protocols and records per room",
        },
        1,
        1,
        1,
      ],
      [
        { de: "Basis-Reporting", en: "Basic reporting" },
        {
          de: "Belegung und Einnahmen auf einen Blick",
          en: "Occupancy and income at a glance",
        },
        1,
        1,
        1,
      ],
    ],
  },
  {
    group: { de: "Vermietung", en: "Letting" },
    rows: [
      [
        { de: "coliviq-Anzeige", en: "coliviq listing" },
        {
          de: "Eigene Anzeigenseite pro Zimmer",
          en: "Its own listing page per room",
        },
        1,
        1,
        1,
      ],
      [
        { de: "Bewerbungen sammeln", en: "Collect applications" },
        {
          de: "Alle Anfragen in einer Pipeline statt im Postfach",
          en: "Every enquiry in one pipeline instead of your inbox",
        },
        1,
        1,
        1,
      ],
      [
        {
          de: "Digitale Verträge & Signatur",
          en: "Digital contracts & signature",
        },
        {
          de: "Rechtssicher unterschreiben, ohne Papier",
          en: "Legally binding signatures, no paper",
        },
        1,
        1,
        1,
      ],
      [
        { de: "Externe Anzeigen", en: "External listings" },
        {
          de: "Automatisch auf allen gängigen Portalen",
          en: "Automatically on every major portal",
        },
        0,
        1,
        1,
      ],
      [
        {
          de: "KI-Vorfilterung mit Empfehlung",
          en: "AI pre-screening with a recommendation",
        },
        {
          de: "Bewerbungen geprüft und vorsortiert",
          en: "Applications checked and pre-sorted",
        },
        0,
        1,
        1,
      ],
      [
        { de: "Smartes Leerstandsmanagement", en: "Smart vacancy management" },
        {
          de: "Neuvermietung startet vor Vertragsende",
          en: "Re-letting starts before the contract ends",
        },
        0,
        1,
        1,
      ],
      [
        { de: "Digitales Übergabeprotokoll", en: "Digital handover protocol" },
        {
          de: "Zustand mit Fotos festhalten",
          en: "Record condition with photos",
        },
        0,
        1,
        1,
      ],
      [
        { de: "Mieterauswahl", en: "Tenant selection" },
        {
          de: "Wir wählen den passenden Mieter aus",
          en: "We pick the right tenant",
        },
        0,
        0,
        1,
      ],
      [
        {
          de: "Besichtigung, Auswahl & Übergabe",
          en: "Viewing, selection & handover",
        },
        {
          de: "Übernehmen wir komplett — digital abgewickelt",
          en: "We take it on entirely — handled digitally",
        },
        0,
        0,
        1,
      ],
      [
        { de: "Schlüsselübergabe", en: "Key handover" },
        { de: "Organisieren wir für Sie", en: "We organise it for you" },
        0,
        0,
        1,
      ],
      [
        {
          de: "Neuvermietung komplett übernommen",
          en: "Re-letting taken on end to end",
        },
        {
          de: "Vom Auszug bis zum neuen Vertrag",
          en: "From move-out to the new contract",
        },
        0,
        0,
        1,
      ],
      [
        { de: "Dynamische Preise", en: "Dynamic pricing" },
        {
          de: "Zimmerpreis passt sich der Nachfrage an",
          en: "Room price adapts to demand",
        },
        0,
        0,
        1,
      ],
    ],
  },
  {
    group: { de: "Zahlungen", en: "Payments" },
    rows: [
      [
        { de: "Manuelle Zahlungskontrolle", en: "Manual payment checks" },
        {
          de: "Eingänge selbst prüfen und abhaken",
          en: "Check and tick off incoming payments yourself",
        },
        1,
        1,
        1,
      ],
      [
        { de: "Open Banking", en: "Open banking" },
        {
          de: "Zahlungen werden automatisch zugeordnet",
          en: "Payments are matched automatically",
        },
        0,
        1,
        1,
      ],
      [
        { de: "SEPA-Lastschrift", en: "SEPA direct debit" },
        {
          de: "Miete wird automatisch eingezogen",
          en: "Rent is collected automatically",
        },
        0,
        1,
        1,
      ],
      [
        { de: "Automatisiertes Mahnwesen", en: "Automated dunning" },
        {
          de: "Erinnerungen laufen ohne Ihr Zutun",
          en: "Reminders run without you lifting a finger",
        },
        0,
        1,
        1,
      ],
      [
        { de: "Stripe-Zahlungen", en: "Stripe payments" },
        {
          de: "Karte und weitere Methoden, z. B. für ausländische Mieter",
          en: "Card and other methods, e.g. for tenants from abroad",
        },
        0,
        0,
        1,
      ],
      [
        { de: "SEV-Übernahme", en: "Take on the SEV" },
        {
          de: "Wir übernehmen die Sondereigentumsverwaltung",
          en: "We take on the separate-property management",
        },
        0,
        0,
        1,
      ],
    ],
  },
  {
    group: { de: "Betrieb & Service", en: "Operations & service" },
    rows: [
      [
        { de: "Mieter-Community", en: "Tenant community" },
        {
          de: "Chat und Events in der Mieter-App",
          en: "Chat and events in the tenant app",
        },
        0,
        1,
        1,
      ],
      [
        {
          de: "Automatische Wohnungsgeberbestätigung",
          en: "Automatic landlord confirmation",
        },
        { de: "Anmeldung ohne Nachfragen", en: "Registration without chasing" },
        0,
        1,
        1,
      ],
      [
        { de: "KI-Ticketing", en: "AI ticketing" },
        {
          de: "Anliegen kommen sortiert, Antwort vorbereitet",
          en: "Issues arrive sorted, with a reply prepared",
        },
        0,
        1,
        1,
      ],
      [
        {
          de: "KI-Ticketing mit Bearbeitung",
          en: "AI ticketing, handled for you",
        },
        {
          de: "Wir bearbeiten die Anliegen für Sie",
          en: "We work the issues for you",
        },
        0,
        0,
        1,
      ],
      [
        { de: "Handwerkermanagement", en: "Contractor management" },
        {
          de: "Beauftragung und Nachverfolgung übernehmen wir",
          en: "We handle briefing and follow-up",
        },
        0,
        0,
        1,
      ],
    ],
  },
];

/* ------------------------------------------------------------------ */

export const ONBOARDING: {
  when: Bilingual;
  name: Bilingual;
  body: Bilingual;
}[] = [
  {
    when: { de: "Schritt 1", en: "Step 1" },
    name: { de: "Objekt anlegen", en: "Add the property" },
    body: {
      de: "Wohnung und Zimmer werden angelegt — von Ihnen, gemeinsam im Call oder komplett durch uns.",
      en: "The flat and its rooms get set up — by you, together on a call, or entirely by us.",
    },
  },
  {
    when: { de: "Schritt 2", en: "Step 2" },
    name: { de: "Bestand übernehmen", en: "Migrate what exists" },
    body: {
      de: "Laufende Mietverhältnisse, Kautionen und Dokumente migrieren wir für Sie.",
      en: "We migrate running tenancies, deposits and documents for you.",
    },
  },
  {
    when: { de: "Schritt 3", en: "Step 3" },
    name: { de: "Zahlungen anbinden", en: "Connect payments" },
    body: {
      de: "Konto verknüpfen — Mieteingänge werden ab sofort automatisch zugeordnet.",
      en: "Link the account — incoming rent is matched automatically from then on.",
    },
  },
  {
    when: { de: "Schritt 4", en: "Step 4" },
    name: { de: "Vermieten", en: "Let the rooms" },
    body: {
      de: "Erstes Zimmer ausschreiben, Bewerber prüfen, Vertrag digital abschließen. Ab dann läuft alles — Sie müssen sich um nichts mehr kümmern.",
      en: "List the first room, review applicants, close the contract digitally. From then on it runs — you need not touch it again.",
    },
  },
];

export const SAFETY: { name: Bilingual; body: Bilingual }[] = [
  {
    name: { de: "Miete kommt nicht", en: "The rent does not arrive" },
    body: {
      de: "Mahnwesen läuft automatisch, Sie sehen offene Posten je Zimmer. In der Sorglos-Verwaltung übernehmen wir die Kommunikation.",
      en: "Dunning runs automatically and you see open items per room. On Fully managed we take over the conversation.",
    },
  },
  {
    name: { de: "Schaden im Zimmer", en: "Damage in the room" },
    body: {
      de: "Digitale Übergabeprotokolle mit Fotos und Unterschrift belegen den Zustand — Grundlage für jede Kautionsabrechnung.",
      en: "Digital handover protocols with photos and a signature evidence the condition — the basis for every deposit settlement.",
    },
  },
  {
    name: { de: "Mieter melden ein Problem", en: "A tenant reports a problem" },
    body: {
      de: "Das KI-Ticketsystem priorisiert Anliegen und leitet sie weiter, statt sie im Postfach liegen zu lassen.",
      en: "The AI ticket system prioritises issues and routes them on, instead of leaving them in an inbox.",
    },
  },
  {
    name: {
      de: "Mieter zieht kurzfristig aus",
      en: "A tenant leaves at short notice",
    },
    body: {
      de: "Nachmietersuche startet automatisch mit der Kündigung, das Zimmer geht sofort wieder online.",
      en: "The search for a replacement starts automatically with the notice, and the room goes back online at once.",
    },
  },
];

export const PARTNERS = [
  "ImmoScout24",
  "Kleinanzeigen",
  "WG-Gesucht",
  "Spacest",
  "FlatsForFriends",
  "HousingAnywhere",
  "Amber",
  "Wunderflats",
  "DocuSign",
  "Stripe",
  "KeyNest",
];

export const TRUST: { name: Bilingual; body: Bilingual }[] = [
  {
    name: { de: "Kein Umstellungsrisiko", en: "No switching risk" },
    body: {
      de: "Bestehende Verträge und Kautionen übernehmen wir für Sie.",
      en: "We take existing contracts and deposits across for you.",
    },
  },
  {
    name: { de: "Rechtssichere Signatur", en: "Legally binding signature" },
    body: {
      de: "Mietverträge werden über DocuSign digital unterschrieben.",
      en: "Leases are signed digitally through DocuSign.",
    },
  },
  {
    name: { de: "Von Betreibern gebaut", en: "Built by operators" },
    body: {
      de: "460+ eigene Zimmer — jede Funktion läuft dort zuerst im Echtbetrieb.",
      en: "460+ rooms of our own — every feature runs there in production first.",
    },
  },
];

/**
 * Landlord testimonials, from the "update wg vermieter page" draft.
 *
 * Names are initials-only and the quotes are the draft's own. The cards carry
 * no portraits: the stock photos that stood in for the people quoted were
 * removed by request (COL-546).
 */
export const LANDLORD_VOICES: {
  name: string;
  role: Bilingual;
  quote: Bilingual;
}[] = [
  {
    name: "Thomas K.",
    role: {
      de: "Vermietet eine WG mit 5 Zimmern",
      en: "Lets a shared flat with 5 rooms",
    },
    quote: {
      de: "Ich hatte früher jedes Jahr zwei, drei Wochen Leerstand. Seit coliviq sind die Zimmer praktisch durchgehend belegt — und ich muss mich um nichts kümmern.",
      en: "I used to have two or three weeks empty every year. Since coliviq the rooms are let more or less continuously — and I don't have to deal with any of it.",
    },
  },
  {
    name: "Sabine M.",
    role: {
      de: "Vermietet ein Haus mit 9 Zimmern",
      en: "Lets a house with 9 rooms",
    },
    quote: {
      de: "Die monatliche Abrechnung ist transparent, Anliegen der Mieter werden schnell gelöst. Ich sehe alles im Dashboard, ohne selbst anrufen zu müssen.",
      en: "The monthly statement is transparent and tenant issues get resolved quickly. I can see everything in the dashboard without having to phone anyone.",
    },
  },
  {
    name: "Markus R.",
    role: { de: "Vermietet zwei Wohnungen", en: "Lets two apartments" },
    quote: {
      de: "Möblierung, Mieterauswahl, Verträge — alles aus einer Hand. Für mich als Eigentümer die entspannteste Vermietung, die ich bisher hatte.",
      en: "Furnishing, tenant selection, contracts — all from one place. As an owner it's the most relaxed letting I've had.",
    },
  },
];

export const OWNER_FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: {
      de: "Was kostet die Verwaltung mit coliviq?",
      en: "What does management with coliviq cost?",
    },
    a: {
      de: "Abgerechnet wird pro Zimmer und Monat: 19 € Selbstverwaltung, 29 € Smartverwaltung, 49 € Sorglos-Verwaltung. Keine Einrichtungsgebühr, alle Pakete laufen 2 Jahre.",
      en: "Billed per room per month: €19 Self-managed, €29 Smart management, €49 Fully managed. No set-up fee, and every package runs for 2 years.",
    },
  },
  {
    q: {
      de: "Kostet mich die Neuvermietung extra?",
      en: "Does re-letting cost extra?",
    },
    a: {
      de: "Nein. Anzeige, Bewerberauswahl, Vertrag und Übergabe sind im Monatspreis enthalten — keine Provision, keine Vermittlungsgebühr, kein Aufschlag pro Mieterwechsel.",
      en: "No. Listing, applicant selection, contract and handover are in the monthly price — no commission, no placement fee, no surcharge per tenant change.",
    },
  },
  {
    q: {
      de: "Was ist, wenn ein Mieter nicht zahlt?",
      en: "What if a tenant does not pay?",
    },
    a: {
      de: "Offene Posten sehen Sie je Zimmer, Zahlungserinnerung und Mahnlauf starten automatisch. In der Sorglos-Verwaltung übernehmen wir die Kommunikation bis zur Klärung; die Kaution ist separat geführt und bei Auszug sofort abrechenbar.",
      en: "You see open items per room, and reminders and dunning start automatically. On Fully managed we take over the conversation until it is resolved; the deposit is held separately and can be settled immediately on move-out.",
    },
  },
  {
    q: {
      de: "Funktioniert das mit meiner bestehenden Hausverwaltung?",
      en: "Does this work with my existing property manager?",
    },
    a: {
      de: "Ja — und meistens ersetzt es sie. Klassische Verwalter rechnen pro Einheit ab und behandeln Ihre WG wie eine Wohnung: ein Vertrag, ein Mieter, eine Abrechnung. coliviq rechnet pro Zimmer, kennt sechs parallele Mietverhältnisse und übernimmt auch die Neuvermietung. Selbst- und Smartverwaltung sind reine Software — Ihre Verwaltung kann einfach darin arbeiten. Bei der Sorglos-Verwaltung brauchen Sie sie in der Regel nicht mehr.",
      en: "Yes — and usually it replaces them. Traditional managers bill per unit and treat your shared flat like one apartment: one contract, one tenant, one statement. coliviq bills per room, understands six parallel tenancies and takes on the re-letting too. Self-managed and Smart management are pure software — your manager can simply work inside them. With Fully managed you generally no longer need one.",
    },
  },
  {
    q: {
      de: "Kann ich zwischen den Paketen wechseln?",
      en: "Can I switch between packages?",
    },
    a: {
      de: "Upgrade jederzeit. Ein Wechsel in ein kleineres Paket ist nach Ablauf der 2 Jahre Laufzeit möglich.",
      en: "Upgrade at any time. Moving to a smaller package is possible once the 2-year term has run.",
    },
  },
  {
    q: {
      de: "Sind die Mietverträge auf Coliving ausgelegt?",
      en: "Are the leases built for coliving?",
    },
    a: {
      de: "Ja, speziell dafür: Einzelmietverträge je Zimmer mit geregelter Mitnutzung der Gemeinschaftsflächen, Hausordnung, Kautionsregelung und Wohnungsgeberbestätigung — juristisch geprüft, nicht ein umgeschriebener Wohnungsmietvertrag.",
      en: "Yes, specifically: single-room leases with agreed shared use of the common areas, house rules, a deposit clause and the landlord confirmation — legally reviewed, not a reworded apartment lease.",
    },
  },
  {
    q: {
      de: "Auf welchen Portalen wird ausgeschrieben?",
      en: "Which portals do you list on?",
    },
    a: {
      de: "Auf coliviq.de und allen gängigen Portalen — von ImmoScout24 und WG-Gesucht bis zu Coliving-Plattformen wie HousingAnywhere, Spacest oder Wunderflats. Einmal gepflegt, überall gleichzeitig ausgespielt.",
      en: "On coliviq.de and every major portal — from ImmoScout24 and WG-Gesucht to coliving platforms like HousingAnywhere, Spacest or Wunderflats. Maintained once, published everywhere at the same time.",
    },
  },
  {
    q: {
      de: "Wie schnell ist ein Zimmer wieder vermietet?",
      en: "How quickly is a room re-let?",
    },
    a: {
      de: "Weil die Neuvermietung mit der Kündigung startet, überschneiden sich Auszug und Einzug in unserem eigenen Portfolio meist nahtlos.",
      en: "Because re-letting starts with the notice, move-out and move-in usually meet seamlessly in our own portfolio.",
    },
  },
  {
    q: {
      de: "Wer ist Vermieter — ich oder coliviq?",
      en: "Who is the landlord — me or coliviq?",
    },
    a: {
      de: "Sie bleiben Vermieter und Eigentümer. In der Sorglos-Verwaltung handeln wir in Ihrem Namen auf Basis einer Vollmacht; die Mietverträge laufen weiter auf Sie.",
      en: "You remain the landlord and the owner. On Fully managed we act in your name under a power of attorney; the leases stay in your name.",
    },
  },
  {
    q: { de: "Wo liegen meine Daten?", en: "Where is my data held?" },
    a: {
      de: "Auf Servern in Deutschland, DSGVO-konform. Einen Auftragsverarbeitungsvertrag bekommen Sie beim Start automatisch.",
      en: "On servers in Germany, GDPR-compliant. You get a data-processing agreement automatically at the start.",
    },
  },
];

/* ------------------------------------------------------------------ */
/* ROI calculator                                                      */
/* ------------------------------------------------------------------ */

/**
 * The ROI model. Structure is the draft's; the per-room prices are the ones
 * the team confirmed (2026-09-28).
 *
 * The draft shipped [8, 16, 49] here, which matched neither pricing column —
 * 8 € was not a discount of anything and 16 € was Selbstverwaltung's annual
 * price being charged for Smartverwaltung, one row out. The calculator bills
 * the ANNUAL plan (its own subtitle says "Preise im Jahresplan"), so it now
 * uses the cards' annual column: 19/29 less 15 % -> 16/25, and Sorglos stays
 * 49 € on both plans by decision.
 */
export const CALC_UNIT_PRICE = [16, 25, 49] as const;
/** Admin hours saved per month, per tier. */
export const CALC_HOURS = [3, 8, 20] as const;
/** What an hour of the owner's admin time is valued at. */
export const CALC_HOURLY_RATE = 35;
/** Fully managed also removes an external manager, priced at 5 % of annual rent. */
export const CALC_EXTERNAL_RATE = 0.05;

export type CalcInput = {
  rooms: number;
  rent: number;
  moves: number;
  days: number;
};

export const CALC_DEFAULTS: CalcInput = {
  rooms: 6,
  rent: 690,
  moves: 4,
  days: 14,
};

export function runCalculator(tier: number, input: CalcInput) {
  const unit = CALC_UNIT_PRICE[tier] ?? CALC_UNIT_PRICE[0];
  const hours = CALC_HOURS[tier] ?? CALC_HOURS[0];
  const rooms = Math.max(0, input.rooms || 0);
  const rent = Math.max(0, input.rent || 0);
  const moves = Math.max(0, input.moves || 0);
  const days = Math.max(0, input.days || 0);

  const costYear = rooms * unit * 12;
  const dayRent = rent / 30;
  const vacancy = moves * days * dayRent;
  const time = hours * 12 * CALC_HOURLY_RATE;
  const external = tier === 2 ? rooms * rent * 12 * CALC_EXTERNAL_RATE : 0;

  return {
    unit,
    hours,
    rooms,
    costYear,
    dayRent,
    vacancy,
    time,
    external,
    net: vacancy + time + external - costYear,
  };
}
